import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Inbox, MessageSquareWarning, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState } from "@/components/ui/EmptyState";
import { useEntityConversations, type EntityMailboxType } from "@/hooks/inbox/useEntityConversations";
import { useDraftIndicators } from "@/hooks/inbox/useDraft";
import { ChannelFilter } from "./ChannelFilter";
import type { ChannelFilterValue } from "./InboxProvider";
import { ConversationListItem } from "./ConversationListItem";
import { MessageThread } from "./MessageThread";
import { SavedMessages } from "./SavedMessages";
import { filterConversations, getConversationTimestamp } from "./utils";
import type { InboxConversation, SaveEntity } from "@/types/inbox";

interface EntityMailboxProps {
	entityType: EntityMailboxType;
	entityId: string;
	entityName?: string;
}

const ListSkeleton: React.FC = () => (
	<div className="space-y-1 py-2" aria-hidden>
		{Array.from({ length: 4 }).map((_, index) => (
			<div key={index} className="flex items-center gap-3 px-3 py-2">
				<Skeleton className="h-10 w-10 shrink-0 rounded-sm" />
				<div className="flex-1 space-y-2">
					<div className="flex justify-between gap-4">
						<Skeleton className="h-3.5 w-2/5" />
						<Skeleton className="h-3 w-10" />
					</div>
					<Skeleton className="h-3 w-4/5" />
				</div>
			</div>
		))}
	</div>
);

interface EntityConversationListProps {
	entityType: EntityMailboxType;
	entityId: string;
	onSelect: (id: string) => void;
}

const EntityConversationList: React.FC<EntityConversationListProps> = ({
	entityType,
	entityId,
	onSelect,
}) => {
	const { t } = useTranslation();
	const [search, setSearch] = useState("");
	const [channelFilter, setChannelFilter] = useState<ChannelFilterValue>("all");
	const { data, isLoading, isError, refetch } = useEntityConversations(entityType, entityId);
	const draftIds = useDraftIndicators();

	const filtered = useMemo(() => {
		const sorted = [...(data || [])].sort(
			(a, b) =>
				new Date(getConversationTimestamp(b) || 0).getTime() -
				new Date(getConversationTimestamp(a) || 0).getTime()
		);
		return filterConversations(sorted, { channel: channelFilter, search });
	}, [data, channelFilter, search]);

	return (
		<div className="flex h-full min-h-0 flex-col">
			<div className="shrink-0 space-y-2 border-b border-border p-3">
				<div className="relative">
					<Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						value={search}
						onChange={(event) => setSearch(event.target.value)}
						placeholder={t("inbox.search", "Search conversations…")}
						className="h-9 pl-8"
						aria-label={t("inbox.search", "Search conversations…")}
					/>
				</div>
				<ChannelFilter value={channelFilter} onChange={setChannelFilter} />
			</div>

			<div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
				{isLoading ? (
					<ListSkeleton />
				) : isError ? (
					<div className="flex h-full flex-col items-center justify-center p-6">
						<EmptyState
							icon={MessageSquareWarning}
							title={t("inbox.entity.errorTitle", "Couldn't load conversations")}
							description={t(
								"inbox.entity.errorDescription",
								"Something went wrong while loading conversations. Try again."
							)}
						/>
						<Button variant="outline" size="sm" className="mt-2" onClick={() => refetch()}>
							{t("inbox.entity.retry", "Retry")}
						</Button>
					</div>
				) : filtered.length === 0 ? (
					<EmptyState
						icon={Inbox}
						title={t("inbox.entity.emptyTitle", "No conversations")}
						description={t(
							"inbox.entity.emptyDescription",
							"No conversations were found for this record."
						)}
						className="h-full"
					/>
				) : (
					<ul role="listbox" aria-label={t("inbox.title", "Inbox")} className="divide-y divide-border/60">
						{filtered.map((conversation) => (
							<ConversationListItem
								key={conversation.id}
								conversation={conversation}
								selected={false}
								hasDraft={draftIds.has(conversation.id)}
								onSelect={onSelect}
							/>
						))}
					</ul>
				)}
			</div>
		</div>
	);
};

/**
 * Unified per-entity mailbox tab (candidate/job/company/match detail pages):
 * the same conversation rows, sanitized threads and composer as the main
 * inbox, plus the entity's saved messages — replaces the old MailboxTab.
 */
export const EntityMailbox: React.FC<EntityMailboxProps> = ({
	entityType,
	entityId,
	entityName,
}) => {
	const { t } = useTranslation();
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const { data } = useEntityConversations(entityType, entityId);

	const selectedConversation: InboxConversation | undefined = useMemo(
		() => (data || []).find((conversation) => conversation.id === selectedId),
		[data, selectedId]
	);

	const saveEntity: SaveEntity | undefined = useMemo(() => {
		if (entityType === "candidate") return { type: "candidates", id: entityId, name: entityName };
		if (entityType === "job") return { type: "jobs", id: entityId, name: entityName };
		if (entityType === "company") return { type: "companies", id: entityId, name: entityName };
		return undefined;
	}, [entityType, entityId, entityName]);

	return (
		<Tabs defaultValue="conversations" className="flex h-[70vh] min-h-[420px] flex-col">
			<TabsList className="w-fit shrink-0">
				<TabsTrigger value="conversations">
					{t("inbox.entity.tabs.conversations", "Conversations")}
				</TabsTrigger>
				<TabsTrigger value="saved">{t("inbox.entity.tabs.saved", "Saved messages")}</TabsTrigger>
			</TabsList>

			<TabsContent
				value="conversations"
				className="mt-3 min-h-0 flex-1 overflow-hidden rounded-sm border border-border bg-background data-[state=inactive]:hidden"
			>
				{selectedConversation ? (
					<MessageThread
						conversation={selectedConversation}
						toolbarId={`entity-mailbox-toolbar-${entityId}`}
						onBack={() => setSelectedId(null)}
						currentEntity={saveEntity}
					/>
				) : (
					<EntityConversationList entityType={entityType} entityId={entityId} onSelect={setSelectedId} />
				)}
			</TabsContent>

			<TabsContent
				value="saved"
				className="mt-3 min-h-0 flex-1 overflow-hidden rounded-sm border border-border bg-background data-[state=inactive]:hidden"
			>
				<SavedMessages entityType={entityType} entityId={entityId} />
			</TabsContent>
		</Tabs>
	);
};
